import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { generateClassName } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';
import pricingData from '@klv/shared/pricing/static/us.json';
import cdpPricing from '@klv/shared/pricing/static/cdp.json';
import reviewsPricing from '@klv/shared/pricing/static/reviews.json';
import marketingAnalyticsPricing from '@klv/shared/pricing/static/analytics.json';
import Slider from '@klv/molecules/slider';
import Icon from '@klv/atoms/icon';
import staticData from './stories/data';
import PricingCalculator from './pricing-calculator';
import MaxContactsElement from './max-contacts';
import PlatformCard from './platform-card';
import PlanCard from './plan-card';

import * as styles from './styles/pricingV2.module.css';
import type { IPricingV2, IEmailPlan, ISMSPlan, IFormattedData, IMessagingType } from './types';
import { withOptimizely, type WithOptimizelyProps } from '@optimizely/react-sdk';
import { Rip } from '../rip';

type TEmailPlans = keyof typeof pricingData['email'];

type TSmsPlans = keyof typeof pricingData['sms'];

export const PricingV2 = withOptimizely(({
  className = '',
  cardOrder,
  MaxContacts,
  cardContent,
  platformCards,
  excludeFreeCard = false,
  hidePlatformCards = false,
  optimizely,
  optimizelyReadyTimeout,
  ...props
}: IPricingV2 & WithOptimizelyProps) => {
  const { t } = useTranslation();

  // Default values for pricing sliders
  const DEFAULT_CONTACTS = 1001;
  const DEFAULT_SMS_CREDITS = 1250;

  const getInitialValues = () => {
    if (typeof window !== 'undefined') {
      const initialParams = new URLSearchParams(window.location.search);
      const paramContacts = Number(initialParams.get('contacts'));
      const paramSmsCredits = Number(initialParams.get('smsCredits'));

      // Use URL values if they exist, otherwise use defaults
      const initialContacts = paramContacts || DEFAULT_CONTACTS;
      const initialSmsCredits = paramSmsCredits || DEFAULT_SMS_CREDITS;

      return { initialContacts, initialSmsCredits };
    }
    return { initialContacts: DEFAULT_CONTACTS, initialSmsCredits: DEFAULT_SMS_CREDITS };
  };

  const { initialContacts, initialSmsCredits } = getInitialValues();

  const [contacts, setContacts] = useState<number>(initialContacts);
  const [smsCredits, setSmsCredits] = useState<number>(initialSmsCredits);

  // Update URL when slider values change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.hash === '#servicePlans') {
        const el = document.getElementById('servicePlans');
        const y = el ? el.getBoundingClientRect().top + window.scrollY - 80 : 0;
        window.scrollTo({ top: y, behavior: 'smooth' });

      }
      url.searchParams.set('contacts', contacts.toString());
      url.searchParams.set('smsCredits', smsCredits.toString());
      window.history.replaceState({}, '', url);
    }
  }, [contacts, smsCredits]);
  const [selectedLocale, setSelectedLocale] = useState<string>('US');
  const [showMaxContacts, setShowMaxContacts] = useState<boolean>(false);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);

  const [messagingType, setMessagingType] = useState<IMessagingType['type']>('sms');

  const handleToggleChange = (type: IMessagingType['type']) => {
    setMessagingType(type);
  };

  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleAllExpanded = () => {
    setIsAllExpanded((prev) => !prev);
    setTimeout(() => {
      buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const { email, emailAndSms, free } = cardContent || {};

  const emailPlans = pricingData.email;
  const smsPlans = pricingData.sms;

  const selectedPlan: IEmailPlan = emailPlans[contacts.toString() as TEmailPlans];
  const selectedSmsPlan: ISMSPlan = smsPlans[smsCredits.toString() as TSmsPlans];


  useEffect(() => {
    if (selectedPlan?.min_customers > 1 && showMaxContacts) {
      setShowMaxContacts(false);
    }
  }, [selectedPlan, showMaxContacts]);

  useEffect(() => {
    optimizely?.track?.('client_pageview');
  }, []);

  const formatData = <T extends object>(
    data: Record<string, T>,
    labelFn: (item: T) => string,
    extraFieldsFn?: (item: T) => object, // Optional function to add extra fields like price
  ): IFormattedData[] => {
    return Object.entries(data).map(([key, item]) => ({
      value: parseInt(key, 10),
      label: labelFn(item),
      ...(extraFieldsFn ? extraFieldsFn(item) : {}), // Add extra fields if provided
    }));
  };

  const emailData = formatData(pricingData.email, (item) =>
    item.max_customers === -1
      ? '250,000+'
      : `${item.min_customers.toLocaleString()} – ${item.max_customers.toLocaleString()}`,
  );
  const analyticsData = formatData(
    marketingAnalyticsPricing.analytics,
    (item) => item.max_customers === -1
      ? `${item.min_customers.toLocaleString()}+`
      : `${item.min_customers.toLocaleString()} – ${item.max_customers.toLocaleString()}`,
    (item) => ({ price: item.price }),
  );

  const smsData = formatData(pricingData.sms, (item) => `${item.send_limit.toLocaleString()}`);
  const cdpData = formatData(cdpPricing.cdp, (item) => `${item.profiles.toLocaleString()}`);
  const reviewsData = formatData(reviewsPricing.reviews, (item) => `${item.monthly_orders.toLocaleString()}`);

  const FreeCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      buttonLink={free?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={free?.buttons?.[0]?.openInNewTab}
      buttonText={free?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={free?.description as string}
      id="pricing--plan-card--free"
      emailSends={500}
      name={t('organisms_pricing_plans_free_name')}
      price={0}
      messagingType={messagingType}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      featuresList={free?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  const EmailCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      buttonLink={email?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={email?.buttons?.[0]?.openInNewTab}
      buttonText={email?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={email?.description as string}
      id="pricing--plan-card--email"
      emailSends={selectedPlan?.send_limit}
      name={t('organisms_pricing_plans_email_name_removeAfterKbos')}
      price={selectedPlan?.price}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      featuresList={email?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  const EmailAndSMSCard = () => (
    <PlanCard
      className={styles.styledPlanCard}
      credits={smsCredits}
      messagingType={messagingType}
      selectedLocale={selectedLocale}
      setSelectedLocale={setSelectedLocale}
      buttonLink={emailAndSms?.buttons?.[0]?.url || t('organisms_navigation_dataActions_signUp_button_href')}
      buttonOpenInNewTab={emailAndSms?.buttons?.[0]?.openInNewTab}
      buttonText={emailAndSms?.buttons?.[0]?.text || t('organisms_pricing_signUp')}
      contacts={contacts}
      description={emailAndSms?.description as string}
      id="pricing--plan-card--email-and-sms"
      emailSends={selectedPlan?.send_limit}
      name={t('organisms_pricing_plans_emailPlusSms_name_removeAfterKbos')}
      price={selectedPlan?.price + selectedSmsPlan.price}
      featuresList={emailAndSms?.featuresList}
      isAllExpanded={isAllExpanded}
      setShowMaxContacts={setShowMaxContacts}
    />
  );

  const adpCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={cdpData}
      name={t('organisms_pricing_plans_advanced_data_platform_headline')}
      description={platformCards?.customerDataPlatform.description}
      featuresList={platformCards?.customerDataPlatform.featuresList}
      subText={t('organisms_pricing_plans_platform_advanced_data_platform_subText')}
      subTextInfoHeader={t('organisms_pricing_plans_platform_advanced_data_platform_subTextInfoHeader')}
      subTextInfo={t('organisms_pricing_plans_advance_data_platform_subTextTooltiptInfo')}
      pricingDescription={t('organisms_pricing_plans_advanced_data_platform_pricingDescription')}
    />
  ), [cdpData, platformCards, t]);

  const renderedRewiewsCard = useMemo(() => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={reviewsData}
      name="Reviews"
      description={platformCards?.reviews.description}
      featuresList={platformCards?.reviews.featuresList}
      subText={t('organisms_pricing_plans_reviews_slider_subText')}
      pricingDescription={t('organisms_pricing_plans_reviews_subText')}
    />
  ), [reviewsData, platformCards, t]);

  const MarketingAnalytics = () => (
    <PlatformCard
      className={styles.styledPlanCard}
      data={analyticsData}
      name={t('organisms_pricing_plans_marketing_analytics_headline')}
      description={platformCards?.marketingAnalytics?.description}
      featuresList={platformCards?.marketingAnalytics.featuresList}
      subText={t('organisms_pricing_plans_marketing_analytics_price_subText')}
      pricingDescription={t('organisms_pricing_plans_marketing_analytics_subText')}
      activeProfiles={contacts}

    />
  );

  return (
    <div className={generateClassName([className, styles.pricingV2])} {...props}>
      <div className="grid">
        <div className={styles.contactsSlider}>
          <Slider
            headline={t('organisms_pricing_contacts_slider_headline')}
            subText={t('organisms_pricing_contacts_slider_subText')}
            subTextInfo={t('organisms_pricing_contacts_slider_subTextInfo')}
            subTextInfoHeader={t('organisms_pricing_contacts_slider_subText')}
            range={emailData}
            value={contacts}
            onChange={setContacts}
          />
        </div>
        <div className={styles.creditsSlider}>
          <Slider
            headline={t('organisms_pricing_credits_slider_headline_removeAfterKbos')}
            subText={t('organisms_pricing_credits_slider_subText_removeAfterKbos')}
            subTextInfo={t('organisms_pricing_credits_slider_subTextInfo_removeAfterKbos')}
            subTextInfoHeader={t('organisms_pricing_credits_slider_subText_removeAfterKbos')}
            range={smsData}
            value={smsCredits}
            onChange={setSmsCredits}
          />
        </div>
        <PricingCalculator
          backgroundColor="light"
          smsCredits={smsCredits}
          onToggleChange={handleToggleChange}
        />
      </div>

      {showMaxContacts ? (
        <MaxContactsElement mixedMedia={staticData.mixedMedia} />
      ) : (
        <div>
          <div className={generateClassName(['grid', styles.plans, excludeFreeCard ? styles.centeredPlans : ''])}>
            {excludeFreeCard ? [
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
              (<><EmailAndSMSCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /></>),
            ][cardOrder || 0] : [
              (<><EmailAndSMSCard /><EmailCard /><FreeCard /></>),
              (<><EmailAndSMSCard /><FreeCard /><EmailCard /></>),
              (<><EmailCard /><EmailAndSMSCard /><FreeCard /></>),
              (<><EmailCard /><FreeCard /><EmailAndSMSCard /></>),
              (<><FreeCard /><EmailAndSMSCard /><EmailCard /></>),
              (<><FreeCard /><EmailCard /><EmailAndSMSCard /></>),
            ][cardOrder || 0]}
          </div>
          <div className={styles.buttonContainer}>
            <button ref={buttonRef} onClick={toggleAllExpanded}>
              {isAllExpanded ? (
                <div className={styles.collapsible}>
                  <div>{t('organisms_pricing_plans_collapsible_button')}</div>
                  <Icon className={styles.iconUp} iconName="ChevronDown" />
                </div>
              ) : (
                <div className={styles.collapsible}>
                  <div>{t('organisms_pricing_plans_expandable_button')}</div>
                  <Icon className={styles.iconDown} iconName="ChevronDown" />
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {!hidePlatformCards && (
        <>
          <Rip
            backgroundColor={platformCards?.topRip.rip?.backgroundColor}
            customPadding={platformCards?.topRip.rip?.customPadding}
            direction={platformCards?.topRip?.rip.direction}
            orientation={platformCards?.topRip.rip?.orientation}
            ripColor={platformCards?.topRip.rip.ripColor}
            ripVariant={platformCards?.topRip.rip?.ripVariant}
          />
          <Wrapper
            backgroundColor={platformCards?.backgroundColor}
            customPadding={{ customPaddingBottom: platformCards?.customPadding?.customPaddingTop, customPaddingTop: platformCards?.customPadding?.customPaddingBottom }}
          >
            <div className={styles.platformCards}>
              <div className={styles.platformCardsTitle}>
                {t('organisms_pricing_plans_platform_headline')}
              </div>
              <div className={`grid ${styles.plans}`}>
                {renderedRewiewsCard}
                <MarketingAnalytics />
                {adpCard}
              </div>
            </div>
          </Wrapper>
          <Rip
            customPadding={platformCards?.bottomRip.rip?.customPadding}
            backgroundColor={platformCards?.bottomRip.rip?.backgroundColor}
            direction={platformCards?.bottomRip?.rip.direction}
            orientation={platformCards?.bottomRip.rip?.orientation}
            ripColor={platformCards?.bottomRip.rip.ripColor}
            ripVariant={platformCards?.topRip.rip?.ripVariant}
          />
        </>
      )}
    </div>
  );
});

